import type { PortalConfig } from './config';

/**
 * Company and terminal identity for the top bar.
 *
 * Mirrors StsApi's `TerminalHeaderResponse`. Every field is nullable in practice — a tenant may have
 * uploaded no logo, and the board must render sensibly regardless.
 */
export interface TerminalHeader {
  companyName: string | null;
  companyAddress: string | null;
  /** Bare file name; resolve through `companyLogoUrl`. */
  companyLogo: string | null;
  branchName: string | null;
  terminalDescription: string | null;
}

/** Must match `AttendancePortalEndpoints.Routes`. */
const HEADER_PATH = '/api/v1/attendance-portal/terminals';

/**
 * Retry backoff, in milliseconds.
 *
 * Unbounded in count (the last value repeats), for the same reason the hub's is: a kiosk must
 * recover on its own after an overnight outage with nobody there to press anything. It settles at
 * five minutes rather than the hub's thirty seconds because a missing header is cosmetic — punches
 * are already arriving — and because the endpoint is strictly rate limited.
 */
const RETRY_DELAYS_MS = [2_000, 10_000, 30_000, 120_000, 300_000];

/** Up to ±25%, so a site's kiosks rebooting together after a power cut stop arriving in lockstep. */
const JITTER = 0.25;

export function companyLogoUrl(config: PortalConfig, fileName: string): string {
  return `${config.stsApiUrl}/files/companyLogo/${encodeURIComponent(fileName)}`;
}

/**
 * Fetches the header for one terminal code.
 *
 * Returns null for every failure — an unknown code, a 429, a network error, a malformed body. The
 * board treats them identically (generic header, keep retrying), and distinguishing them here would
 * only tempt a caller into showing an error on a wall that nobody is standing at.
 */
export async function fetchTerminalHeader(
  config: PortalConfig,
  code: string,
): Promise<TerminalHeader | null> {
  try {
    const response = await fetch(`${config.stsApiUrl}${HEADER_PATH}/${encodeURIComponent(code)}`, {
      // The endpoint is anonymous; sending credentials would trip CORS for no benefit.
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new HeaderRequestFailed(retryAfterMs(response));
    }

    return (await response.json()) as TerminalHeader;
  } catch (error) {
    throw error instanceof HeaderRequestFailed ? error : new HeaderRequestFailed(null);
  }
}

/** Carries the server's own `Retry-After` when it sent one, so a 429 is obeyed rather than guessed at. */
export class HeaderRequestFailed extends Error {
  constructor(readonly retryAfterMs: number | null) {
    super('Terminal header request failed');
  }
}

/**
 * Delay before attempt number `attempt` (0-based), preferring the server's `Retry-After` over our
 * own schedule — the rate limiter already told us exactly how long it wants to be left alone.
 */
export function retryDelayMs(attempt: number, retryAfterMs: number | null): number {
  const base =
    retryAfterMs ?? RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)]!;
  return Math.round(base * (1 + (Math.random() * 2 - 1) * JITTER));
}

function retryAfterMs(response: Response): number | null {
  const header = response.headers.get('Retry-After');
  if (!header) return null;

  // Only the delta-seconds form is produced by the shared rate limiter's OnRejected handler; the
  // HTTP-date form is accepted by the spec but never sent here, so parsing it would be dead code.
  const seconds = Number(header);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : null;
}
